import * as THREE from "three";
import Experience from "../Experience";
import SpriteElement from "./SpriteElement";
import { landingStuff, pathwayItems } from "./sets";
import { gsap } from "gsap";
import type Camera from "../Camera";

type PathwayData = {
  id: string;
  forkID?: string;
  parent?: THREE.Object3D;
} & Record<string, unknown>;

type MaterialLike = THREE.Material & {
  opacity?: number;
  transparent?: boolean;
};
type MaterialCarrier = THREE.Object3D & {
  material?: MaterialLike | MaterialLike[];
};

export default class PathwayItem {
  experience!: Experience;
  scene!: THREE.Scene;
  camera!: Camera;
  data: PathwayData;
  pathway: THREE.Object3D | null = null;

  constructor(data: PathwayData) {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.camera = this.experience.camera;
    this.data = data;
    this.pathway = null;
  }

  async create() {
    const pathway = new THREE.Object3D();
    pathway.name = "pathwayItem";
    if (this.data.forkID) {
      pathway.userData.forkID = this.data.forkID;
      pathway.userData.id = this.data.id;
    } else {
      pathway.userData.id = this.data.id;
    }
    this.data.parent = pathway;
    const pathline = (await this.experience.importPath.regenPathLines(this.data)) as THREE.Object3D;
    const camline = (await this.experience.importPath.importCameraLine(this.data)) as THREE.Object3D;
    pathway.add(pathline);
    pathway.add(camline);
    camline.children.forEach((child) => {
      const material = (child as MaterialCarrier).material;
      if (!material) return;
      const materials = Array.isArray(material) ? material : [material];
      materials.forEach((entry) => {
        entry.opacity = 0;
        entry.transparent = true;
      });
      child.visible = false;
    });
    camline.visible = false;
    pathline.scale.set(0.5, 0.5, 0.5);
    camline.scale.set(0.25, 0.25, 0.25);

    const pathLineBoundingBox = new THREE.Box3().setFromObject(pathline);
    const centerPathLine = pathLineBoundingBox.getCenter(new THREE.Vector3());
    pathline.position.sub(centerPathLine);
    const camLineBoundingBox = new THREE.Box3().setFromObject(camline);
    const centercamLine = camLineBoundingBox.getCenter(new THREE.Vector3());
    camline.position.sub(centercamLine);

    const pathSprite = new SpriteElement(this.data);
    if (pathSprite.label) {
      pathwayItems.push(pathSprite.label);
    }
    landingStuff.push(pathway);
    this.scene.add(pathway);
    this.pathway = pathway;
    if (pathway?.position && this.camera) {
      gsap.to(pathway.quaternion, {
        duration: 1,
        x: 0,
        y: 0,
        z: 0,
        w: 1,
        ease: "power2.out",
      });
    }
    return pathway;
  }
}
